import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { Avatar as DefaultIcon } from '@icons/common';

export interface IAvatar {
  className?: string;
  name?: string;
  url: string | null;
  pulsing?: boolean;
}

const Avatar = ({
  className = '',
  url: propUrl,
  name = '',
  pulsing = false,
}: IAvatar) => {
  const [hasError, setHasError] = useState(false);
  const [url, setUrl] = useState(propUrl);

  useEffect(() => {
    if (propUrl !== url) {
      setHasError(false);
      setUrl(propUrl);
    }
  }, [url, propUrl]);

  const handleError = () => {
    setHasError(true);
  };

  const hasImage = url && !hasError;

  return (
    <StyledAvatar className={className} title={name} pulsing={pulsing}>
      {hasImage ? (
        <img alt={name} onError={handleError} src={url} />
      ) : (
        <DefaultIcon />
      )}
    </StyledAvatar>
  );
};

Avatar.propTypes = {
  className: PropTypes.string,
  name: PropTypes.string,
  url: PropTypes.string,
  pulsing: PropTypes.bool,
};

Avatar.defaultProps = {
  className: '',
  name: '',
  url: '',
  pulsing: false,
};

const StyledAvatar = styled.div<{ pulsing: boolean }>`
  border-radius: 50%;
  height: 40px;
  width: 40px;
  position: relative;

  > * {
    display: block;
    height: 100%;
    width: 100%;
    background: #fff;
    border-radius: 100%;
  }

  ${({ pulsing }) =>
    pulsing &&
    `
    &:before,
    &:after {
      top: 50%;
      left: 50%;
      content: '';
      z-index: -1;
      position: absolute;
      border-radius: 100%;
      animation: pulsing 1.5s infinite ease-in-out;
      background-image: linear-gradient(to top, #00e04b, #00b0b6);
    }

    &:before {
      width: 60px;
      height: 60px;
      opacity: 0.3;
      margin-top: -30px;
      margin-left: -30px;
    }

    &:after {
      width: 48px;
      height: 48px;
      opacity: 0.8;
      margin-top: -24px;
      margin-left: -24px;
    }

    @keyframes pulsing {
      from {transform: scale(.5);}
    }
  `}
`;

export default Avatar;
